// Region resolution: normalized geo ({ country, region }) → consent regime.
// How geo is obtained (edge, CDN, backend, inline config) is the host's business.

// EU member states + Iceland, Liechtenstein, Norway. Some providers report "EU" when unsure.
const EEA = 'AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE IS LI NO EU'.split(' ');

/**
 * Built-in regimes. `match` holds country codes (`DE`), country-region codes (`US-CA`) or `*`.
 * The most specific match wins: `US-CA` before `US` before `*`.
 */
export const DEFAULT_REGIMES = {
  eea: { match: EEA, requireExplicitConsent: true },
  ch: { match: ['CH'], requireExplicitConsent: true },
  uk: { match: ['GB'], requireExplicitConsent: true },
  'us-california': { match: ['US-CA'], requireExplicitConsent: false, showBanner: true, honorGpc: true },
  default: { match: ['*'], requireExplicitConsent: false, showBanner: false },
};

// Placeholders providers use for unknown locations, Tor and anonymous proxies.
const UNKNOWN_COUNTRIES = new Set(['XX', 'ZZ', 'T1', 'A1', 'A2', 'O1']);
const MATCH_TOKEN = /^(\*|[A-Z]{2}(-[A-Z0-9]{1,3})?)$/;
const REGIME_NAME = /^[a-z0-9][a-z0-9-]{0,31}$/i;

/** Privacy-safe fallback when geo is missing or invalid: require explicit consent. */
const FALLBACK = { regime: 'unknown', requireExplicitConsent: true, showBanner: true, honorGpc: false };

/** Upper-cases and validates geo. `region` accepts `CA` or `US-CA`. Null when the country is unusable. */
export function normalizeGeo(geo) {
  if (!geo || typeof geo !== 'object') return null;
  const country = typeof geo.country === 'string' ? geo.country.trim().toUpperCase() : '';
  if (!/^[A-Z]{2}$/.test(country) || UNKNOWN_COUNTRIES.has(country)) return null;
  let region = typeof geo.region === 'string' ? geo.region.trim().toUpperCase() : '';
  if (region.startsWith(`${country}-`)) region = region.slice(country.length + 1);
  return { country, region: /^[A-Z0-9]{1,3}$/.test(region) ? region : null };
}

/**
 * resolveConsentRegion({ country: 'DE', region: 'BY' })
 *   → { regime: 'eea', requireExplicitConsent: true, showBanner: true, honorGpc: false, country: 'DE', region: 'BY' }
 *
 * `regimes` overrides built-ins by name ({ default: { showBanner: true } }) or adds new ones
 * ({ ch: { match: ['CH'] } }). Added regimes are checked before built-ins at the same specificity.
 */
export function resolveConsentRegion(geo, regimes) {
  const location = normalizeGeo(geo);
  if (!location) return { ...FALLBACK, country: null, region: null };

  const rules = buildRules(regimes);
  const { country, region } = location;
  for (const token of [region && `${country}-${region}`, country, '*']) {
    if (!token) continue;
    const rule = rules.find((candidate) => candidate.match.includes(token));
    if (rule) {
      const { regime, requireExplicitConsent, showBanner, honorGpc } = rule;
      return { regime, requireExplicitConsent, showBanner, honorGpc, country, region };
    }
  }
  return { ...FALLBACK, country, region };
}

function buildRules(overrides) {
  const custom = overrides !== null && typeof overrides === 'object' ? overrides : {};
  const isBuiltIn = (name) => Object.prototype.hasOwnProperty.call(DEFAULT_REGIMES, name);
  const names = [...Object.keys(custom).filter((name) => !isBuiltIn(name)), ...Object.keys(DEFAULT_REGIMES)];

  const rules = [];
  for (const name of names) {
    const override = custom[name] !== null && typeof custom[name] === 'object' ? custom[name] : {};
    const definition = { ...(isBuiltIn(name) ? DEFAULT_REGIMES[name] : {}), ...override };
    if (!REGIME_NAME.test(name) || !Array.isArray(definition.match)) continue;
    // Anything not explicitly opt-out requires consent, and explicit consent always shows the banner.
    const requireExplicitConsent = definition.requireExplicitConsent !== false;
    rules.push({
      regime: name,
      match: definition.match.map((token) => String(token).trim().toUpperCase()).filter((token) => MATCH_TOKEN.test(token)),
      requireExplicitConsent,
      showBanner: requireExplicitConsent || definition.showBanner === true,
      honorGpc: definition.honorGpc === true,
    });
  }
  return rules;
}
